import { useKy } from 'boot/ky'
import { baseLogementFields } from 'src/stores/useLogementsStore'
import SessionStorageService from 'src/services/sessionStorageService'
import { consumeHomePrefetch } from 'src/utils/homePrefetch'

/**
 * Service API pour les logements
 * Gère toutes les interactions avec l'API Directus pour les logements
 */
export const LogementService = {
  /**
   * Récupère la liste des logements avec filtres Directus
   * @param {Object} params - Paramètres de recherche
   * @param {Object} params.filters - Filtres Directus (ex: { status: { _eq: 'published' } })
   * @param {string} params.sort - Tri (ex: '-awlo_rating,-note')
   * @param {Array<string>|string} params.fields - Champs à récupérer
   * @param {Object} params.deep - Relations imbriquées
   * @param {number} params.page - Page (défaut: 1)
   * @param {number} params.limit - Limite par page (défaut: 12)
   * @param {boolean} params.isPublic - Requête publique (sans token)
   * @param {AbortSignal} params.signal - Signal d'annulation
   * @returns {Promise<Object>} { data: Array, meta: Object }
   */
  async getAll(params = {}) {
    const prefetched = await consumeHomePrefetch(params)
    if (prefetched) {
      return prefetched
    }

    const {
      filters = {},
      sort = '-awlo_rating,-note,-date_created',
      fields,
      deep,
      page = 1,
      limit = 12,
      isPublic = false,
      signal,
    } = params

    // Convertir _geospatial_search en geolocalisation._intersects pour l'API Directus
    let finalFilters = filters && Object.keys(filters).length > 0 ? { ...filters } : {}
    if (finalFilters._geospatial_search) {
      const geo = finalFilters._geospatial_search
      const lat = Number(geo.latitude)
      const lng = Number(geo.longitude)
      const radius = Number(geo.radius) || 1000
      delete finalFilters._geospatial_search
      if (!Number.isNaN(lat) && !Number.isNaN(lng) && radius > 0 && radius <= 50000) {
        const geoGeometry = LogementService._createCirclePolygon(lat, lng, radius)
        finalFilters.geolocalisation = { _intersects: geoGeometry }
        if (geo.date && geo.date.from && geo.date.to) {
          const dateFilter = {
            reservation: {
              _none: {
                _and: [
                  { date_d_arrivee: { _nnull: true } },
                  { date_de_depart: { _nnull: true } },
                  { date_d_arrivee: { _lt: geo.date.to } },
                  { date_de_depart: { _gt: geo.date.from } },
                ],
              },
            },
          }
          finalFilters = { _and: [finalFilters, dateFilter] }
        }
      }
    }

    // Gestion des champs par défaut
    const baseFields =
      fields && fields !== '*'
        ? typeof fields === 'string'
          ? fields.split(',')
          : fields
        : [...baseLogementFields]

    const finalFields = [...baseFields]

    // Construction des searchParams pour ky
    const searchParams = {
      sort,
      meta: 'filter_count',
      limit,
      page,
    }

    // Gestion du mode auth : ajouter favoris si nécessaire
    const finalDeep = deep ? JSON.parse(JSON.stringify(deep)) : {}
    const mode = isPublic ? 'public' : LogementService._detectMode()

    if (mode === 'auth') {
      // Ajouter 'favoris.id' seulement s'il n'est pas déjà demandé
      if (!finalFields.includes('favoris.id')) {
        finalFields.push('favoris.id')
      }

      // Assurer que l'objet favoris existe dans deep
      if (!finalDeep.favoris) {
        finalDeep.favoris = {}
      }

      // Ajouter le filtre pour l'utilisateur courant
      finalDeep.favoris._filter = {
        user_created: {
          _eq: '$CURRENT_USER',
        },
      }
    }

    searchParams.fields = finalFields.join(',')

    // Sérialisation conditionnelle des filtres et deep
    if (finalFilters && Object.keys(finalFilters).length > 0) {
      searchParams.filter = JSON.stringify(finalFilters)
    }

    if (Object.keys(finalDeep).length > 0) {
      searchParams.deep = JSON.stringify(finalDeep)
    }

    // Appel API avec gestion public/auth
    const { get, getPublic } = useKy()
    const kyMethod = isPublic ? getPublic : get

    return await kyMethod('items/logements', {
      searchParams,
      signal,
    })
  },

  /**
   * Récupère un logement par ID
   * @param {string|number} id - ID du logement
   * @param {Object} options - Options
   * @param {string} options.fields - Champs à récupérer
   * @param {Object} options.deep - Relations imbriquées
   * @param {boolean} options.isPublic - Requête publique (sans token)
   * @param {AbortSignal} options.signal - Signal d'annulation
   * @returns {Promise<Object>} Logement
   */
  async getOne(id, options = {}) {
    const {
      fields,
      deep,
      isPublic = false,
      signal,
    } = options

    const searchParams = {}

    if (fields) {
      searchParams.fields = fields
    }

    // Sérialisation conditionnelle de deep
    if (deep && Object.keys(deep).length > 0) {
      searchParams.deep = JSON.stringify(deep)
    }

    // Appel API avec gestion public/auth
    const { get, getPublic } = useKy()
    const mode = isPublic ? 'public' : LogementService._detectMode()
    const kyMethod = mode === 'public' ? getPublic : get

    const response = await kyMethod(`items/logements/${id}`, {
      searchParams,
      signal,
    })

    return response?.data || null
  },

  /**
   * Calcule le centroïde d'un GeoJSON Polygon pour usage comme point de référence.
   * @param {Object} polygon - GeoJSON Polygon
   * @returns {{ lat: number, lng: number }}
   */
  _getPolygonCentroid(polygon) {
    if (!polygon?.coordinates?.[0]?.length) return { lat: 0, lng: 0 }
    const ring = polygon.coordinates[0]
    let sumLng = 0
    let sumLat = 0
    const n = ring.length - 1 // exclure le point de fermeture
    for (let i = 0; i < n; i++) {
      sumLng += ring[i][0]
      sumLat += ring[i][1]
    }
    return { lat: sumLat / n, lng: sumLng / n }
  },

  /**
   * Crée un polygone GeoJSON approximant un cercle (pour filtre _intersects Directus).
   * @param {number} lat - Latitude du centre
   * @param {number} lng - Longitude du centre
   * @param {number} radiusMeters - Rayon en mètres
   * @param {number} segments - Nombre de points du polygone (défaut: 32)
   * @returns {Object} GeoJSON Polygon { type: 'Polygon', coordinates: [[[lng, lat], ...]] }
   */
  _createCirclePolygon(lat, lng, radiusMeters, segments = 32) {
    const coords = []
    const latRad = (lat * Math.PI) / 180
    const metersPerDegLat = 111320
    const metersPerDegLng = 111320 * Math.cos(latRad)
    for (let i = 0; i <= segments; i++) {
      const angle = (2 * Math.PI * i) / segments
      const dy = (radiusMeters / metersPerDegLat) * Math.cos(angle)
      const dx = (radiusMeters / metersPerDegLng) * Math.sin(angle)
      coords.push([lng + dx, lat + dy])
    }
    return { type: 'Polygon', coordinates: [coords] }
  },

  /**
   * Recherche géospatiale via l'API Directus items/logements (filtre geolocalisation _intersects).
   * Utilise le même endpoint que getAll, avec un filtre géométrique (cercle ou polygon).
   * @param {Object} params - Paramètres de recherche
   * @param {number} params.latitude - Latitude
   * @param {number} params.longitude - Longitude
   * @param {number} params.radius - Rayon en mètres (défaut: 1000)
   * @param {Object} params.polygon - GeoJSON Polygon optionnel (prioritaire sur radius)
   * @param {Object} params.date - Filtre de dates { from: string, to: string }
   * @param {string} params.placeName - Nom du lieu (fallback texte si géospatial = 0)
   * @param {Object} params.filters - Filtres Directus (type_de_logements, status, etc.)
   * @param {number} params.page - Page (défaut: 1)
   * @param {number} params.limit - Limite par page (défaut: 12)
   * @param {string} params.fields - Champs à récupérer
   * @param {string} params.sort - Tri
   * @param {Object} params.deep - Relations imbriquées
   * @param {AbortSignal} params.signal - Signal d'annulation
   * @returns {Promise<Object>} { data: Array, meta: Object }
   */
  async searchByLocation(params) {
    const {
      latitude,
      longitude,
      radius = 1000,
      polygon = null,
      date,
      placeName = null,
      filters: filtersParam = null,
      page = 1,
      limit = 12,
      fields,
      sort = '-awlo_rating,-note,-date_created',
      deep,
      signal,
    } = params

    let lat = typeof latitude === 'number' ? latitude : Number(latitude)
    let lng = typeof longitude === 'number' ? longitude : Number(longitude)

    let geoGeometry = null
    if (polygon) {
      const valid = polygon.type === 'Polygon' && Array.isArray(polygon.coordinates)
    }
    if (polygon && polygon.type === 'Polygon' && Array.isArray(polygon.coordinates)) {
      geoGeometry = polygon
      const centroid = this._getPolygonCentroid(polygon)
      if (isNaN(lat) || isNaN(lng)) {
        lat = centroid.lat
        lng = centroid.lng
      }
    }

    if (isNaN(lat) || isNaN(lng)) {
      throw new Error(
        'Coordonnées invalides : latitude et longitude doivent être des nombres'
      )
    }
    if (lat < -90 || lat > 90) {
      throw new Error('Latitude invalide : doit être entre -90 et 90')
    }
    if (lng < -180 || lng > 180) {
      throw new Error('Longitude invalide : doit être entre -180 et 180')
    }

    if (!geoGeometry) {
      const rad = typeof radius === 'number' ? radius : Number(radius)
      if (isNaN(rad) || rad <= 0 || rad > 50000) {
        throw new Error('Radius invalide : doit être entre 1 et 50000 mètres')
      }
      geoGeometry = this._createCirclePolygon(lat, lng, rad)
    }

    const filterParts = []

    if (filtersParam && typeof filtersParam === 'object' && Object.keys(filtersParam).length > 0) {
      filterParts.push(filtersParam)
    }

    // Géospatial OU fallback texte (place_name/city_name) si placeName fourni
    const geoFilter = { geolocalisation: { _intersects: geoGeometry } }
    if (placeName && typeof placeName === 'string' && placeName.trim().length > 0) {
      const textFallback = {
        _or: [
          { place_name: { _icontains: placeName.trim() } },
          { city_name: { _icontains: placeName.trim() } },
        ],
      }
      filterParts.push({ _or: [geoFilter, textFallback] })
    } else {
      filterParts.push(geoFilter)
    }

    if (date && date.from && date.to) {
      filterParts.push({
        reservation: {
          _none: {
            _and: [
              { date_d_arrivee: { _nnull: true } },
              { date_de_depart: { _nnull: true } },
              { date_d_arrivee: { _lt: date.to } },
              { date_de_depart: { _gt: date.from } },
            ],
          },
        },
      })
    }

    const finalFilter = filterParts.length === 1 ? filterParts[0] : { _and: filterParts }

    const searchParams = {
      filter: JSON.stringify(finalFilter),
      sort,
      limit,
      page,
      meta: 'filter_count',
      fields: fields || baseLogementFields.join(','),
    }
    if (deep && Object.keys(deep).length > 0) {
      searchParams.deep = JSON.stringify(deep)
    }

    const { getPublic } = useKy()
    return await getPublic('items/logements', {
      searchParams,
      signal,
    })
  },

  /**
   * Détecte le mode d'authentification (public ou auth)
   * @private
   * @returns {string} 'public' ou 'auth'
   */
  _detectMode() {
    const hasToken = SessionStorageService.hasSession()

    if (!hasToken) {
      return 'public'
    }

    // Le token existe, on vérifie le rôle
    try {
      const sessionUser = SessionStorageService.getUser()
      const roleName = sessionUser?.role?.name

      if (roleName === 'Voyageur') {
        return 'auth' // Mode AUTH seulement pour les voyageurs
      } else {
        return 'public' // Les autres rôles voient la version publique
      }
    } catch (e) {
      return 'public' // En cas d'erreur, fallback sécurisé en public
    }
  },
}

